import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeModeContext } from '@luigiclaudio/ga-baseline-ui/theme-provider';
import BaselineThemeMode from '@luigiclaudio/ga-baseline-ui/theme-provider/src/components/BaselineThemeMode';
import { resolveThemeMode } from '../utils/themeBootstrap.cjs';

const LIGHT_MODE = 'lightMode';
const DARK_MODE = 'darkMode';

const readStoredThemeMode = () => {
    try {
        return window.localStorage.getItem('theme');
    } catch (error) {
        return undefined;
    }
};

const writeStoredThemeMode = (themeMode) => {
    try {
        window.localStorage.setItem('theme', themeMode);
    } catch (error) {
        // Theme selection still works for this document when storage is unavailable.
    }
};

const getSystemThemeQuery = () => {
    try {
        return window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : undefined;
    } catch (error) {
        return undefined;
    }
};

const applyDocumentTheme = (themeMode) => {
    document.documentElement.dataset.themeMode = themeMode;
    document.documentElement.style.colorScheme = themeMode === DARK_MODE ? 'dark' : 'light';
};

const SsrThemeProvider = ({ children, theme }) => {
    // A fixed server snapshot makes the server and first hydration render identical.
    // The head bootstrap has already applied the effective mode to CSS before paint.
    const [themeMode, setThemeMode] = useState(LIGHT_MODE);

    useEffect(() => {
        const systemThemeQuery = getSystemThemeQuery();
        let themeReadyFrame;
        const syncTheme = () => {
            const nextThemeMode = resolveThemeMode(
                readStoredThemeMode(),
                Boolean(systemThemeQuery?.matches),
            );
            setThemeMode(nextThemeMode);
            applyDocumentTheme(nextThemeMode);
            window.cancelAnimationFrame(themeReadyFrame);
            themeReadyFrame = window.requestAnimationFrame(() => {
                document.documentElement.dataset.reactThemeReady = 'true';
            });
        };

        syncTheme();
        systemThemeQuery?.addEventListener?.('change', syncTheme);

        return () => {
            window.cancelAnimationFrame(themeReadyFrame);
            systemThemeQuery?.removeEventListener?.('change', syncTheme);
        };
    }, []);

    const toggleThemeMode = useCallback(() => {
        setThemeMode((currentThemeMode) => {
            const nextThemeMode = currentThemeMode === LIGHT_MODE ? DARK_MODE : LIGHT_MODE;
            writeStoredThemeMode(nextThemeMode);
            applyDocumentTheme(nextThemeMode);
            return nextThemeMode;
        });
    }, []);

    const value = useMemo(
        () => ({
            themeMode,
            toggleThemeMode,
        }),
        [themeMode, toggleThemeMode],
    );

    return (
        <ThemeModeContext.Provider value={value}>
            <BaselineThemeMode theme={theme}>{children}</BaselineThemeMode>
        </ThemeModeContext.Provider>
    );
};

SsrThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
    theme: PropTypes.shape({}).isRequired,
};

export default SsrThemeProvider;
